import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarGrid, type CellRenderArgs } from '../components/CalendarGrid';
import { ApiClientError, fetchAvailability, type AvailabilityCell } from '../lib/api';
import {
  addMonthsToKey,
  clampMonth,
  firstOfMonth,
  isValidMonthKey,
  lastOfMonth,
  monthKey,
} from '../lib/months';
import { todayInZurich } from '../lib/today';
import { CELL_CLASSES, cellTone } from '../lib/availability';
import { useMonthSwipe } from '../lib/useMonthSwipe';

export function PublicCalendar() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();

  const today = todayInZurich();
  const todayMonth = monthKey(today);
  const horizonMonth = addMonthsToKey(todayMonth, 12);

  const urlMonth = params.get('month');
  const initial = isValidMonthKey(urlMonth) ? urlMonth : todayMonth;
  const [month, setMonthRaw] = useState<string>(clampMonth(initial, todayMonth, horizonMonth));

  const setMonth = (next: string) => {
    const clamped = clampMonth(next, todayMonth, horizonMonth);
    setMonthRaw(clamped);
    const np = new URLSearchParams(params);
    np.set('month', clamped);
    setParams(np, { replace: true });
  };

  // Keep URL and state in sync if URL changes externally.
  useEffect(() => {
    if (isValidMonthKey(urlMonth)) {
      const clamped = clampMonth(urlMonth, todayMonth, horizonMonth);
      if (clamped !== month) setMonthRaw(clamped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlMonth]);

  // Ensure ?month= is always present.
  useEffect(() => {
    if (!isValidMonthKey(urlMonth)) {
      const np = new URLSearchParams(params);
      np.set('month', month);
      setParams(np, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [cells, setCells] = useState<Map<string, AvailabilityCell> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);

  useEffect(() => {
    const ctl = new AbortController();
    setLoading(true);
    setError(null);

    // Public API rejects anything before today; clamp the request range.
    const rawFrom = firstOfMonth(month);
    const rawTo = lastOfMonth(month);
    const from = rawFrom < today ? today : rawFrom;
    const to = rawTo < today ? today : rawTo;

    if (from > to) {
      setCells(new Map());
      setLoading(false);
      return () => ctl.abort();
    }

    fetchAvailability(from, to, ctl.signal)
      .then((rows) => {
        const m = new Map<string, AvailabilityCell>();
        for (const r of rows) m.set(r.date, r);
        setCells(m);
      })
      .catch((e: unknown) => {
        if (ctl.signal.aborted) return;
        if (e instanceof ApiClientError) setError(t(`errors.${e.code}`, e.message));
        else setError(t('errors.ERR_INTERNAL'));
      })
      .finally(() => {
        if (!ctl.signal.aborted) setLoading(false);
      });

    return () => ctl.abort();
  }, [month, today, t]);

  const monthLabel = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y!, m! - 1, 1));
    return new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'de', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(d);
  }, [month, i18n.resolvedLanguage]);

  const canGoBack = month > todayMonth;
  const canGoFwd = month < horizonMonth;

  const swipe = useMonthSwipe({
    onPrev: canGoBack ? () => setMonth(addMonthsToKey(month, -1)) : undefined,
    onNext: canGoFwd ? () => setMonth(addMonthsToKey(month, 1)) : undefined,
  });

  const renderCell = ({ date, inMonth }: CellRenderArgs) => {
    if (!inMonth) return null;
    if (date < today) {
      return <span className="text-slate-300 dark:text-slate-700">·</span>;
    }
    const cell = cells?.get(date);
    if (!cell) {
      return loading ? <span className="text-slate-300 dark:text-slate-700">…</span> : null;
    }
    const tone = cellTone(
      cell.total_beds,
      cell.total_beds - cell.beds_available,
      cell.has_duty,
    );
    const hasBookings = cell.beds_available < cell.total_beds;
    const interesting =
      tone === 'closed' || hasBookings || cell.is_override || cell.has_duty;
    if (!interesting) return null;

    // Closed day: fill the whole cell with the closed pattern. Mobile shows
    // no text; desktop overlays the localised "Geschlossen" label.
    if (tone === 'closed') {
      return (
        <span
          className={
            'absolute inset-0 rounded-md border flex items-center justify-center ' +
            'text-xs sm:text-sm font-medium pointer-events-none ' +
            CELL_CLASSES.closed
          }
          title={t('publicCalendar.closed')}
        >
          <span className="hidden sm:inline">{t('publicCalendar.closed')}</span>
        </span>
      );
    }

    const showBedBadge = hasBookings || cell.is_override;
    const badgeBase =
      'block w-full min-w-0 rounded-sm border text-[10px] sm:text-xs font-medium ' +
      'leading-tight px-0.5 py-0.5 text-center break-words hyphens-auto tabular-nums';
    const countText = t('publicCalendar.outOf', {
      free: cell.beds_available,
      total: cell.total_beds,
    });
    const dutyNamesText = cell.duty_names.join(', ');

    // Staffed: paint the whole cell sky-tinted (replaces the old "Hut
    // staffed" pill). The warden name(s) inside the cell carry the WHO;
    // the availability badge still overlays on top in its own occupancy
    // colour so capacity info isn't lost on staffed days.
    if (tone === 'staffed') {
      const innerTone = cellTone(
        cell.total_beds,
        cell.total_beds - cell.beds_available,
        false,
      );
      return (
        <>
          <span
            className={
              'absolute inset-0 rounded-md border pointer-events-none ' +
              CELL_CLASSES.staffed
            }
          />
          <div className="relative z-10 flex flex-col gap-0.5 w-full min-w-0">
            <span
              className={
                'block w-full min-w-0 text-[10px] sm:text-xs font-semibold ' +
                'leading-tight text-center break-words text-sky-900 dark:text-sky-200'
              }
              title={dutyNamesText}
            >
              {dutyNamesText}
            </span>
            {showBedBadge && (
              <span
                className={`${badgeBase} ${CELL_CLASSES[innerTone]}`}
                title={countText}
              >
                {countText}
              </span>
            )}
          </div>
        </>
      );
    }

    // Unstaffed, non-closed: plain availability badge.
    return (
      <span className={`${badgeBase} ${CELL_CLASSES[tone]}`} title={countText}>
        {countText}
      </span>
    );
  };


  return (
    <section aria-labelledby="public-cal-title">
      <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
        <h1 id="public-cal-title" className="text-xl sm:text-2xl font-semibold capitalize">
          {monthLabel}
        </h1>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={!canGoBack}
            onClick={() => setMonth(addMonthsToKey(month, -1))}
            className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm font-medium bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
            aria-label={t('common.previous')}
          >
            ←
          </button>
          <button
            type="button"
            disabled={!canGoFwd}
            onClick={() => setMonth(addMonthsToKey(month, 1))}
            className="rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm font-medium bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
            aria-label={t('common.next')}
          >
            →
          </button>
        </div>
      </div>

      <div {...swipe} className="touch-pan-y select-none">
        <CalendarGrid month={month} cellRenderer={renderCell} />
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-600 dark:text-slate-400">
        <Legend swatchClass="bg-emerald-50 border-emerald-300 dark:bg-emerald-950 dark:border-emerald-700">
          {t('publicCalendar.legend.green')}
        </Legend>
        <Legend swatchClass="bg-amber-50 border-amber-300 dark:bg-amber-950 dark:border-amber-700">
          {t('publicCalendar.legend.amber')}
        </Legend>
        <Legend swatchClass="bg-rose-50 border-rose-300 dark:bg-rose-950 dark:border-rose-700">
          {t('publicCalendar.legend.full')}
        </Legend>
        <Legend swatchClass="bg-[repeating-linear-gradient(45deg,theme(colors.slate.200)_0,theme(colors.slate.200)_4px,theme(colors.slate.100)_4px,theme(colors.slate.100)_8px)] border-slate-300 dark:bg-[repeating-linear-gradient(45deg,theme(colors.slate.700)_0,theme(colors.slate.700)_4px,theme(colors.slate.800)_4px,theme(colors.slate.800)_8px)] dark:border-slate-600">
          {t('publicCalendar.legend.closed')}
        </Legend>
        <Legend swatchClass="bg-sky-50 border-sky-300 dark:bg-sky-950 dark:border-sky-700">
          {t('publicCalendar.legend.duty')}
        </Legend>
      </div>

      <div className="mt-3 flex justify-end flex-wrap gap-2">
        <Link
          to="/gast"
          className={
            'inline-flex items-center gap-1.5 rounded-md border ' +
            'border-slate-300 bg-white text-slate-700 ' +
            'dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 ' +
            'px-3 py-1.5 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800'
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4"
            aria-hidden="true"
          >
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
          {t('nav.externalEntry')}
        </Link>
        <button
          type="button"
          onClick={() => setSubscribeOpen(true)}
          className={
            'inline-flex items-center gap-1.5 rounded-md border ' +
            'border-slate-300 bg-white text-slate-700 ' +
            'dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 ' +
            'px-3 py-1.5 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800'
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4"
            aria-hidden="true"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {t('publicCalendar.subscribe.title')}
        </button>
      </div>

      {subscribeOpen && <CalendarSubscribeModal onClose={() => setSubscribeOpen(false)} />}

      {error && (
        <p role="alert" className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2">
          {error}
        </p>
      )}
    </section>
  );
}

function CalendarSubscribeModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const httpsUrl = `${origin}/api/public/calendar.ics`;
  const webcalUrl = httpsUrl.replace(/^https?:\/\//, 'webcal://');
  const googleUrl = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`;
  const outlookUrl =
    `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(httpsUrl)}` +
    `&name=${encodeURIComponent('Bärenfang Hütte')}`;

  // ESC closes; matches the QrCodeModal pattern.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(httpsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browsers without clipboard API — fall back to a select-all hint.
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscribe-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 dark:bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-sm w-full"
      >
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-4 py-3">
          <h3
            id="subscribe-title"
            className="text-sm font-semibold text-slate-800 dark:text-slate-100"
          >
            {t('publicCalendar.subscribe.title')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="rounded-md p-1.5 text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="w-5 h-5"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-600 dark:text-slate-400">
            {t('publicCalendar.subscribe.help')}
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              href={webcalUrl}
              className="rounded-md border border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 px-3 py-1.5 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              {t('publicCalendar.subscribe.apple')}
            </a>
            <a
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 px-3 py-1.5 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              {t('publicCalendar.subscribe.google')}
            </a>
            <a
              href={outlookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 px-3 py-1.5 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              {t('publicCalendar.subscribe.outlook')}
            </a>
          </div>
          <div className="flex items-stretch gap-2">
            <input
              type="text"
              readOnly
              value={httpsUrl}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 min-w-0 rounded-md border border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 px-2 py-1.5 text-xs font-mono"
            />
            <button
              type="button"
              onClick={() => void onCopy()}
              className="rounded-md border border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 px-3 py-1.5 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              {copied
                ? t('publicCalendar.subscribe.copied')
                : t('publicCalendar.subscribe.copyUrl')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Legend({ swatchClass, children }: { swatchClass: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded-sm border ${swatchClass}`} />
      {children}
    </span>
  );
}
